import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, matchPath } from 'react-router-dom';
import { client } from '../helpers/api-client';
import { resolveAuthToken } from '../helpers/authToken';
import { extractSessionStateMessage, SESSION_STATE_EVENT } from '../helpers/sessionStateMessages';
import LogRocket from 'logrocket';
import setupLogRocketReact from 'logrocket-react';

const AppointmentContext = createContext();

export const getStoredLoggedUser = () => {
  try {
    return JSON.parse(window.localStorage.getItem('loggedUser'));
  } catch {
    return null;
  }
};

// The API authenticates ws/chat/<room>/ with the DRF token passed as ?token=
// (browsers cannot set headers on WebSocket requests).
export const buildRoomSocketUrl = (roomName) => {
  const base = `${process.env.REACT_APP_CORE_WS_URL}/ws/chat/${roomName}/`;
  const token = resolveAuthToken()?.token;
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
};

// Non-room routes that share the /start/:roomName shape.
const RESERVED_PATHS = ['welcome', 'clinician', 'patient', 'end-of-test', 'error'];

// Keep localStorage.room_name in sync with /start/<room> before the appointment is fetched.
// Welcome/ResponsiveWelcome also do this, but on non-desktop devices they now mount only
// after the role is known, which is after this read.
export const syncRoomNameFromUrl = (pathname = window.location.pathname) => {
  const match = matchPath(`${process.env.PUBLIC_URL}/:roomName`, pathname);
  const roomName = match?.params?.roomName;
  if (roomName && !RESERVED_PATHS.includes(roomName)) {
    window.localStorage.setItem('room_name', roomName);
  }
};

export const useAppointment = () => {
  const state = useContext(AppointmentContext);
  return {
    ...state,
  };
};

export const AppointmentProvider = ({ children }) => {
  let navigate = useNavigate();
  const [appointment, setAppointment] = useState(null);
  const [messageQueue, setMessageQueue] = useState([]);
  const [loggedUser, setLoggedUser] = useState(null);
  const [userResolved, setUserResolved] = useState(false);

  const logInLogRocket = () => {
    LogRocket.init(`${process.env.REACT_APP_LOGROCKET_TOKEN}`);
    setupLogRocketReact(LogRocket);
    LogRocket.identify(`${process.env.REACT_APP_LOGROCKET_TOKEN}`, {
      name: loggedUser.full_name,
      email: loggedUser.email,
      role: loggedUser.role,
    });
  };

  const getAppointmentData = async () => {
    syncRoomNameFromUrl();
    const room_name = window.localStorage.getItem('room_name');
    let currentUser;
    try {
      currentUser = await client(`api/users/current`);
      setLoggedUser(currentUser);
    } finally {
      setUserResolved(true);
    }

    const currentAppointment = await client(`api/appointments/${room_name}/`).catch(() => {
      navigate(`${process.env.PUBLIC_URL}/error`, { state: { error: 'This appointment is not available.' } });
    });

    if (currentAppointment === undefined) {
      return;
    }

    if (currentAppointment?.status !== 'open') {
      navigate(`${process.env.PUBLIC_URL}/error`, {
        state: { error: 'This session has been closed. Close your browser to exit.', title: 'Session closed' },
      });
    } else {
      const appointmentToken = await client(`api/appointments/${room_name}/appointment_token/`);
      await websocketManagerConnector.init(currentAppointment.room_name);
      const appointmentData = {
        currentUser: currentUser,
        currentAppointment: currentAppointment,
        appointmentToken: appointmentToken,
        websocket: websocketManagerConnector,
      };
      setAppointment(appointmentData);

      return appointmentData;
    }
  };

  const websocketManagerConnector = {
    timeout: 60,
    room_name: 'default',
    connection: null,
    heartBeatInterval: null,
    init: async (room_name) => {
      websocketManagerConnector.room_name = room_name;
      if (websocketManagerConnector.heartBeatInterval) {
        window.clearInterval(websocketManagerConnector.heartBeatInterval);
      }
      return new Promise(function (resolve) {
        var server = new WebSocket(buildRoomSocketUrl(room_name));
        server.onopen = function () {
          websocketManagerConnector.timeout = 60;
          websocketManagerConnector.heartBeatInterval = setInterval(() => {
            const messageObj = {
              message: {
                content: { type: 'heartBeat' },
              },
            };
            websocketManagerConnector.postMessage(messageObj);
          }, 10000);
          websocketManagerConnector.connection = server;
          websocketManagerConnector.messageListener();
          resolve(server);
        };
        server.onerror = function (err) {
          console.log('on error: ', err);
          websocketManagerConnector?.connection?.close();
        };
        server.onclose = function (err) {
          console.log('on close: ', err);
          if (--websocketManagerConnector.timeout) {
            setTimeout(function () {
              websocketManagerConnector.init(websocketManagerConnector.room_name);
            }, 1000);
          } else {
            window.alert('The platform lost connection with the server. The page will be reloaded');
            window.location.reload();
          }
        };
      });
    },
    postMessage: (messageObj) => {
      var messageString = JSON.stringify({
        message: messageObj,
      });
      if (websocketManagerConnector?.connection?.readyState && websocketManagerConnector.connection.readyState !== WebSocket.OPEN) {
        websocketManagerConnector?.connection?.close();
      }
      websocketManagerConnector.connection.send(messageString);
    },
    messageListener: function () {
      websocketManagerConnector.connection.onmessage = function (e) {
        var messageData = JSON.parse(e.data);
        // Server-side session state sync ({"type":"session.state",...}) is not part of the
        // {"message":...} relay protocol: hand it to SessionStateProvider instead of the queue.
        const sessionState = extractSessionStateMessage(messageData);
        if (sessionState) {
          window.dispatchEvent(new CustomEvent(SESSION_STATE_EVENT, { detail: sessionState }));
          return;
        }
        setMessageQueue((prev) => [...prev, messageData]);
      };
    },
  };

  useEffect(() => {
    if (!appointment) {
      getAppointmentData();
    }
  }, [appointment]);

  useEffect(() => {
    if (loggedUser && process.env.REACT_APP_LOGROCKET_TOKEN !== undefined) {
      logInLogRocket();
    }
  }, [loggedUser]);

  const updateMessageQueue = useCallback(() => {
    return setMessageQueue((prev) => prev.slice(1));
  }, []);

  // Role comes from api/users/current; localStorage.loggedUser (legacy dashboard only) is a fallback.
  const userRole = loggedUser?.role ?? getStoredLoggedUser()?.role ?? null;

  return (
    <AppointmentContext.Provider value={{ appointment, messageQueue, updateMessageQueue, loggedUser, userRole, userResolved }}>
      {children}
    </AppointmentContext.Provider>
  );
};
